import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { listOnboardingSessions } from "@/features/portal/staff-api";
import type { OnboardingSession } from "@/features/portal/staff-api";
import { formatShortDate } from "@/lib/format";

/** Control Center "Onboarding" tab — the firm's pending client-onboarding
 * sessions (mirrors the legacy Pending Onboarding feed). Renders bare (no Card)
 * for embedding in the tab body. */
export function DashboardOnboarding() {
  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    listOnboardingSessions("PENDING")
      .then((result) => active && setSessions(result))
      .catch(() => active && toast.error("Couldn't load onboarding."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No pending onboarding.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="[&>th]:text-muted-foreground [&>th]:text-xs [&>th]:font-semibold [&>th]:tracking-wide [&>th]:uppercase">
          <TableHead>Client</TableHead>
          <TableHead>Sent</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sessions.map((s) => (
          <TableRow key={s.id} className="hover:bg-accent">
            <TableCell className="font-medium">
              <Link
                to={`/client/${s.client}/profile`}
                className="hover:text-primary hover:underline"
              >
                {s.clientName ?? "Client"}
              </Link>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatShortDate(s.createdAt)}
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatShortDate(s.expiresAt)}
            </TableCell>
            <TableCell>
              <Badge variant={s.isPending ? "secondary" : "outline"}>
                {s.isPending ? "Pending" : "Expired"}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
